import type {
  EntityOrSuggestionRef,
  OpenDraftAssignment,
  ProcessDraftAssignment
} from '@bliks-io/contracts';

/**
 * Shape accepted by `<ProcessFlowEditor openAssignments={…} />` in the draft
 * editor. Structurally matches the UI-kit's `OpenAssignmentPlaceholder`; the
 * `kind: 'assignment'` tag selects the editable rendering path (the UI-kit
 * also supports `'open-assignment'`, which is used only by the published-
 * process view).
 *
 * `assignedTo` is the `EntityOrSuggestionRef` from the draft assignment — the
 * builder resolves entity refs through the editor's `assigneeLookup` to a
 * populated user/team for the swimlane.
 */
export type PlaceholderAssignment = Omit<OpenDraftAssignment, 'assignedTo' | 'assignedToModel'> & {
  kind: 'assignment';
  assignedTo?: EntityOrSuggestionRef;
  assignedToModel?: OpenDraftAssignment['assignedToModel'];
};

/**
 * Adapt draft assignments for the process-flow editor's placeholder slot.
 *
 * `ProcessDraftAssignment.assignedTo` is an `EntityOrSuggestionRef` discrim-
 * inated union. The UI-kit's builder consumes the ref directly: entity refs
 * are resolved through `assigneeLookup`; suggestion refs land in the
 * "unassigned" lane until a follow-up adds suggestion-chip rendering.
 *
 * Status is read from the frozen `statusSnapshot`; rows without one (legacy
 * or draft-authored) default to `'pending'`.
 */
export function buildPlaceholderAssignments(
  draftAssignments: ProcessDraftAssignment[]
): PlaceholderAssignment[] {
  return draftAssignments.map((a) => ({
    kind: 'assignment',
    _id: String(a._id),
    stableStepId: String(a.stableStepId),
    assignedTo: a.assignedTo,
    assignedToModel: a.assignedToModel,
    inputs: a.inputs ?? [],
    status: a.statusSnapshot ?? 'pending'
  }));
}
